<template>
    <div class="admin-dashboard">
        <!-- Stats Cards -->
        <div class="stats-grid">
            <div class="stat-card" v-for="stat in statCards" :key="stat.label"
                :class="{ 'is-clickable': stat.clickable }" @click="stat.clickable && goDownloadLogs()">
                <div class="stat-icon-box" :style="{ background: stat.gradient }">
                    <el-icon :size="24" color="#fff">
                        <component :is="stat.icon" />
                    </el-icon>
                </div>
                <div class="stat-info">
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                </div>
                <div class="stat-badge" v-if="stat.badge">{{ stat.badge }}</div>
            </div>
        </div>

        <!-- Charts Row -->
        <el-row :gutter="20" class="chart-row">
            <el-col :span="16">
                <el-card shadow="never" class="chart-card">
                    <template #header>
                        <div class="chart-header">
                            <span class="chart-title">📊 访问与下载趋势</span>
                            <el-radio-group v-model="trendDays" size="small" @change="fetchStats">
                                <el-radio-button :label="7">7天</el-radio-button>
                                <el-radio-button :label="15">15天</el-radio-button>
                                <el-radio-button :label="30">30天</el-radio-button>
                            </el-radio-group>
                        </div>
                    </template>
                    <div class="dual-charts">
                        <div ref="visitChartRef" class="chart-container"></div>
                        <div ref="downloadChartRef" class="chart-container"></div>
                    </div>
                </el-card>
            </el-col>
            <el-col :span="8">
                <el-card shadow="never" class="chart-card server-card">
                    <template #header>
                        <div class="chart-header">
                            <span class="chart-title">🖥️ 服务器状态</span>
                            <el-tag type="success" size="small" effect="dark" round>运行中</el-tag>
                        </div>
                    </template>
                    <div class="server-info-list">
                        <div class="info-row">
                            <span class="info-label">运行环境</span>
                            <span class="info-value">Go + Gin</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">数据库</span>
                            <span class="info-value">SQLite</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">前端框架</span>
                            <span class="info-value">Vue 3</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">API 端口</span>
                            <span class="info-value">:8082</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">当日请求量</span>
                            <span class="info-value highlight">{{ stats.todayVisits || 0 }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">今日新增用户</span>
                            <span class="info-value highlight">{{ stats.newUsersToday || 0 }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">系统时间</span>
                            <span class="info-value">{{ currentTime }}</span>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>

        <!-- Page Tracking Stats Row -->
        <el-row :gutter="20" class="chart-row" style="margin-top: 20px;">
            <el-col :span="12">
                <el-card shadow="never" class="chart-card">
                    <template #header>
                        <div class="chart-header">
                            <span class="chart-title">🔥 访问最多页面 (Top 5)</span>
                        </div>
                    </template>
                    <el-table :data="stats.topVisitedPages || []" style="width: 100%" stripe border>
                        <el-table-column prop="title" label="页面标题" />
                        <el-table-column prop="path" label="路径" show-overflow-tooltip />
                        <el-table-column prop="count" label="被访问次数" width="120" align="center">
                            <template #default="scope">
                                <el-tag type="danger">{{ scope.row.count }}</el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>
            <el-col :span="12">
                <el-card shadow="never" class="chart-card">
                    <template #header>
                        <div class="chart-header">
                            <span class="chart-title">⏱️ 平均停留最长 (Top 5)</span>
                        </div>
                    </template>
                    <el-table :data="stats.topDurationPages || []" style="width: 100%" stripe border>
                        <el-table-column prop="title" label="页面标题" />
                        <el-table-column prop="path" label="路径" show-overflow-tooltip />
                        <el-table-column label="平均停留时间" width="150" align="center">
                            <template #default="scope">
                                <span style="font-weight: bold; color: #409EFF;">{{ Math.round(scope.row.avgTime) }}
                                    秒</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>
        </el-row>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { User, Document, Collection, ChatLineRound, View, TrendCharts, Download, Connection } from '@element-plus/icons-vue'
import api from '@/utils/api'
import * as echarts from 'echarts'

const router = useRouter()
const stats = ref({})
const visitChartRef = ref(null)
const downloadChartRef = ref(null)
let visitChartInstance = null
let downloadChartInstance = null
const currentTime = ref('')
let timer = null

const trendDays = ref(7)

const statCards = computed(() => [
    {
        label: '注册用户',
        value: stats.value.userCount || 0,
        icon: 'User',
        gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
        badge: `+${stats.value.newUsersToday || 0} 今日`
    },
    {
        label: '题目总数',
        value: stats.value.questionCount || 0,
        icon: 'Document',
        gradient: 'linear-gradient(135deg, #f093fb, #f5576c)'
    },
    {
        label: '试卷总数',
        value: stats.value.paperCount || 0,
        icon: 'Collection',
        gradient: 'linear-gradient(135deg, #4facfe, #00f2fe)'
    },
    {
        label: '题单总数',
        value: stats.value.listCount || 0,
        icon: 'TrendCharts',
        gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)'
    },
    {
        label: '论坛帖子',
        value: stats.value.postCount || 0,
        icon: 'ChatLineRound',
        gradient: 'linear-gradient(135deg, #fa709a, #fee140)'
    },
    {
        label: '总下载量',
        value: stats.value.downloadCount || 0,
        icon: 'Download',
        gradient: 'linear-gradient(135deg, #43e97b, #38f9d7)',
        clickable: true
    },
    {
        label: '今日流量 (UV/PV)',
        value: `${stats.value.todayUV || 0} / ${stats.value.todayPV || 0}`,
        icon: 'View',
        gradient: 'linear-gradient(135deg, #a18cd1, #fbc2eb)'
    },
    {
        label: '当前在线',
        value: stats.value.onlineUsers || 0,
        icon: 'Connection',
        gradient: 'linear-gradient(135deg, #ff9a9e, #fecfef)'
    }
])

const fetchStats = async () => {
    try {
        const res = await api.get('/api/admin/stats', {
            params: { days: trendDays.value }
        })
        stats.value = res.data || {}
        await nextTick()
        renderCharts()
    } catch (e) {
        console.error('Failed to fetch stats', e)
    }
}

const renderCharts = () => {
    renderVisitChart()
    renderDownloadChart()
}

const renderVisitChart = () => {
    if (!visitChartRef.value || !stats.value.visitTrend) return

    if (visitChartInstance) visitChartInstance.dispose()
    visitChartInstance = echarts.init(visitChartRef.value)

    const trend = stats.value.visitTrend || []
    visitChartInstance.setOption({
        title: { text: '页面访问统计', textStyle: { fontSize: 14, color: '#64748b' }, left: 'center' },
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: ['访客人数 (UV)', '访问次数 (PV)'] },
        grid: { top: 40, right: 50, bottom: 50, left: 50 },
        xAxis: {
            type: 'category',
            data: trend.map(t => t.day),
            axisLine: { lineStyle: { color: '#e0e0e0' } },
            axisLabel: { color: '#666' }
        },
        yAxis: [
            {
                type: 'value',
                name: 'UV',
                splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
                axisLabel: { color: '#667eea' }
            },
            {
                type: 'value',
                name: 'PV',
                splitLine: { show: false },
                axisLabel: { color: '#f6ad55' }
            }
        ],
        series: [
            {
                name: '访客人数 (UV)',
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 8,
                yAxisIndex: 0,
                data: trend.map(t => t.uv),
                lineStyle: { width: 3, color: '#667eea' },
                itemStyle: { color: '#667eea', borderWidth: 2, borderColor: '#fff' },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
                        { offset: 1, color: 'rgba(102, 126, 234, 0.02)' }
                    ])
                }
            },
            {
                name: '访问次数 (PV)',
                type: 'line',
                smooth: true,
                symbol: 'diamond',
                symbolSize: 8,
                yAxisIndex: 1,
                data: trend.map(t => t.pv),
                lineStyle: { width: 3, color: '#f6ad55', type: 'dashed' },
                itemStyle: { color: '#f6ad55' }
            }
        ]
    })
}

const renderDownloadChart = () => {
    if (!downloadChartRef.value || !stats.value.downloadTrend) return

    if (downloadChartInstance) downloadChartInstance.dispose()
    downloadChartInstance = echarts.init(downloadChartRef.value)

    const trend = stats.value.downloadTrend || []
    downloadChartInstance.setOption({
        title: { text: '试卷下载量', textStyle: { fontSize: 14, color: '#64748b' }, left: 'center' },
        tooltip: { trigger: 'axis' },
        grid: { top: 40, right: 20, bottom: 30, left: 50 },
        xAxis: {
            type: 'category',
            data: trend.map(t => t.day),
            axisLine: { lineStyle: { color: '#e0e0e0' } },
            axisLabel: { color: '#666' }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
            axisLabel: { color: '#999' }
        },
        series: [{
            name: '下载量',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            data: trend.map(t => t.count),
            lineStyle: { width: 3, color: '#10b981' },
            itemStyle: { color: '#10b981', borderWidth: 2, borderColor: '#fff' },
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
                    { offset: 1, color: 'rgba(16, 185, 129, 0.02)' }
                ])
            }
        }]
    })
}

const goDownloadLogs = () => {
    router.push('/admin/download-logs')
}

const formatTime = (time) => {
    if (!time) return '-'
    const date = new Date(time)
    return date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0') + ' ' +
        String(date.getHours()).padStart(2, '0') + ':' +
        String(date.getMinutes()).padStart(2, '0') + ':' +
        String(date.getSeconds()).padStart(2, '0')
}

const updateTime = () => {
    const now = new Date()
    currentTime.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
}

onMounted(() => {
    fetchStats()
    updateTime()
    timer = setInterval(updateTime, 1000)
    window.addEventListener('resize', renderCharts)
})

onUnmounted(() => {
    if (visitChartInstance) visitChartInstance.dispose()
    if (downloadChartInstance) downloadChartInstance.dispose()
    if (timer) clearInterval(timer)
    window.removeEventListener('resize', renderCharts)
})
</script>

<style scoped>
.admin-dashboard {
    padding: 0;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.stat-card {
    background: #fff;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    border: 1px solid #f0f2f5;
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
}

.stat-card.is-clickable {
    cursor: pointer;
}

.stat-card.is-clickable:active {
    transform: translateY(-2px);
}

.stat-icon-box {
    width: 50px;
    height: 50px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-info {
    flex: 1;
}

.stat-value {
    font-size: 26px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.2;
}

.stat-label {
    font-size: 13px;
    color: #94a3b8;
    margin-top: 2px;
}

.stat-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 11px;
    font-weight: 700;
    color: #22c55e;
    background: #f0fdf4;
    padding: 2px 8px;
    border-radius: 100px;
}

.chart-row {
    margin-top: 8px;
}

.chart-card {
    border-radius: 16px;
    border: 1px solid #f0f2f5;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.chart-title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
}

.chart-container {
    height: 320px;
    width: 100%;
}

.dual-charts {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.server-card {
    height: 100%;
}

.server-info-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.info-label {
    font-size: 13px;
    color: #94a3b8;
}

.info-value {
    font-size: 14px;
    font-weight: 600;
    color: #475569;
}

.info-value.highlight {
    color: #3b82f6;
    font-weight: 800;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.username {
    font-size: 14px;
    font-weight: 500;
}

.pagination-row {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}
</style>
